import { Link, Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { sessionUnreachable, signOutAndLeave, useSession } from '../../lib/auth.js'
import { SessionUnreachable } from '../../components/FullPageNotice.js'
import { api } from '../../lib/api.js'
import { AppFrame } from '../../classroom/Shell.js'
import { LogoMark, Skeleton } from '../../classroom/ui.js'
import { PartBoundary } from '../student/PartBoundary.js'

/**
 * Studio — Diana's side — in the classroom style (Studio-*.png): a dark
 * 236px sidebar, the same field, panel and bars as the student panel.
 *
 * The menu is the handoff's canonical one. „Sprzedaż” waits for the shop
 * (decision 2a): it stays out until a payment provider exists.
 */
const nav = [
  { to: '/studio', label: 'Strona główna', end: true },
  { to: '/studio/kalendarz', label: 'Kalendarz', count: 'pending' as const },
  { to: '/studio/klienci', label: 'Uczniowie' },
  { to: '/studio/kursy', label: 'Kursy' },
  { to: '/studio/rozliczenia', label: 'Rozliczenia' },
  { to: '/studio/cennik', label: 'Cennik' },
  { to: '/studio/produkty', label: 'Produkty' },
  { to: '/studio/wiadomosci', label: 'Wiadomości', count: 'leads' as const },
  { to: '/studio/strona', label: 'Strona i treści' },
]

export function StudioLayout() {
  const location = useLocation()
  const sessionState = useSession()
  const { data: session, isPending } = sessionState
  const isAdmin = (session?.user as { role?: string } | undefined)?.role === 'ADMIN'
  const dash = useQuery({ queryKey: ['studio', 'dashboard'], queryFn: api.studio.dashboard, enabled: isAdmin })

  if (isPending) {
    return (
      <div className="cl flex h-dvh items-center justify-center">
        <div className="w-72">
          <Skeleton />
        </div>
      </div>
    )
  }

  if (sessionUnreachable(sessionState)) return <SessionUnreachable />
  if (!session?.user) return <Navigate to="/login" replace />

  /*
   * A student who wandered in. Every API call under /api/studio would answer
   * 403, so without this they get the panel's chrome filled with errors and
   * no way to tell what went wrong. Send them where their own things are.
   */
  if (!isAdmin) return <Navigate to="/panel" replace />

  // The teacher view is a full-screen player, like the student's: no Studio menu around it.
  if (location.pathname.startsWith('/studio/lekcja/')) return <Outlet />

  const counts = { pending: dash.data?.pendingRequests || 0, leads: dash.data?.newLeads || 0 }
  const out = signOutAndLeave

  return (
    <AppFrame studio>
      <aside className="hidden h-full w-[236px] shrink-0 flex-col gap-[18px] overflow-y-auto bg-class-studio-side px-3 py-5 text-class-white desk:flex">
        <Link to="/studio" className="flex items-center gap-2.5 px-2 !text-class-white">
          <LogoMark />
          <span className="leading-tight">
            <span className="block font-class-display text-[18px]">Studio</span>
            <span className="text-class-label text-class-studio-link">naukapolskiego.online</span>
          </span>
        </Link>

        <nav aria-label="Studio" className="flex flex-col gap-0.5 text-class-gloss">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [
                  'flex min-h-10 items-center gap-2.5 rounded-class-sm px-3',
                  isActive ? 'bg-white/[0.14] font-bold !text-class-white' : 'font-semibold !text-class-studio-link hover:bg-white/[0.06]',
                ].join(' ')
              }
            >
              {item.label}
              {item.count && counts[item.count] > 0 && (
                <span className="ml-auto text-class-label font-semibold text-class-studio-link">{counts[item.count]}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto flex flex-col gap-1.5 text-class-gloss">
          <Link to="/panel" className="flex min-h-10 items-center rounded-class-sm bg-white/[0.08] px-3 font-semibold !text-class-white hover:bg-white/[0.14]">
            Zobacz jako uczeń ›
          </Link>
          <span className="flex min-h-9 items-center px-3 text-class-studio-link">
            <NavLink
              to="/studio/konto"
              className={({ isActive }) => (isActive ? 'font-bold !text-class-white' : '!text-class-studio-link hover:!text-class-white')}
            >
              Konto
            </NavLink>
            <span className="px-1.5">·</span>
            <button type="button" onClick={out} className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-class-studio-link hover:text-class-white">
              Wyloguj
            </button>
          </span>
        </div>
      </aside>

      {/* Phone: the menu as a scrolling strip; Studio is a desk tool, but it should not break on a phone. */}
      <nav
        aria-label="Studio"
        className="flex shrink-0 gap-1 overflow-x-auto bg-class-studio-side px-2 py-2 text-class-label desk:hidden"
      >
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              [
                'shrink-0 rounded-class-sm px-3 py-2 font-semibold whitespace-nowrap',
                isActive ? 'bg-white/[0.14] !text-class-white' : '!text-class-studio-link',
              ].join(' ')
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <PartBoundary
        resetKey={location.pathname}
        fallback={
          <div className="cl flex flex-1 items-center justify-center p-10">
            <p className="m-0 font-semibold text-class-wrong">✗ Tej części nie udało się wyświetlić. Odśwież stronę.</p>
          </div>
        }
      >
        <Outlet />
      </PartBoundary>
    </AppFrame>
  )
}
